import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { ProbateWizard, autosaveKeyForEstate } from '../ProbateWizard';
import { ProCaseReportView } from './ProCaseReportView';
import { proCaseApi, ProCase } from './proCaseApi';
import { Button } from '../ui';

const ESTATE_ID_KEY = 'probate_current_estate_id';

type Route =
    | { kind: 'cases' }
    | { kind: 'new' }
    | { kind: 'detail'; caseId: string }
    | { kind: 'audit'; caseId: string }
    | { kind: 'report'; caseId: string };

/**
 * Parses /pro/* paths into a route descriptor. The Pro workspace deliberately
 * uses pathname routing (no library) to stay consistent with the rest of the
 * app — see App.tsx for the same pattern. Unrecognised /pro paths fall back
 * to the cases list rather than 404 so a stale bookmark still lands the user
 * somewhere sensible.
 */
export function parseProRoute(pathname: string): Route {
    const trimmed = pathname.replace(/^\/pro\/?/, '').replace(/\/+$/, '');
    if (!trimmed || trimmed === 'cases') return { kind: 'cases' };
    if (trimmed === 'cases/new') return { kind: 'new' };
    const m = trimmed.match(/^cases\/([^/]+)(?:\/(audit|report))?$/);
    if (m) {
        const [, caseId, sub] = m;
        if (sub === 'audit') return { kind: 'audit', caseId };
        if (sub === 'report') return { kind: 'report', caseId };
        return { kind: 'detail', caseId };
    }
    return { kind: 'cases' };
}

function navigate(path: string) {
    window.history.pushState({}, '', path);
    // Custom event so the App-level router re-reads the URL without a full reload.
    window.dispatchEvent(new PopStateEvent('popstate'));
}

// ─────────────────────────────────────────────────────────────────────
// Top chrome — shared across all pro pages
// ─────────────────────────────────────────────────────────────────────

function ProTopBar() {
    const { user, logout } = useAuth();
    return (
        <header className="bg-white border-b border-rule">
            <div className="flex items-center justify-between px-8 h-[52px]">
                <button
                    onClick={() => navigate('/pro/cases')}
                    className="flex items-baseline gap-3.5 hover:opacity-80 transition-opacity"
                >
                    <span className="font-serif text-[18px] text-ink leading-none">
                        Estate<span className="italic">clarity</span>
                    </span>
                    <span className="text-[9px] tracking-eyebrow uppercase text-ink3 border-l border-rule pl-3.5">
                        Pro workspace
                    </span>
                </button>
                <div className="flex items-center gap-4 text-[12px]">
                    <span className="text-ink3">{user?.email}</span>
                    <span className="text-[10px] tracking-eyebrow uppercase text-accent border border-rule px-2 py-0.5">
                        {user?.role}
                    </span>
                    <button onClick={logout} className="text-ink3 hover:text-ink">Sign out</button>
                </div>
            </div>
        </header>
    );
}

// ─────────────────────────────────────────────────────────────────────
// /pro/cases — list of cases owned by the signed-in pro user
// ─────────────────────────────────────────────────────────────────────

function ProCasesList() {
    const [cases, setCases] = useState<ProCase[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        proCaseApi.list().then(setCases).catch(e => setError(e.message));
    }, []);

    return (
        <main className="max-w-[960px] mx-auto px-8 py-12">
            <div className="flex items-baseline justify-between mb-8">
                <div>
                    <h1 className="font-serif text-[28px] text-ink">Cases</h1>
                    <p className="text-[12px] text-ink3 mt-1">Cases you have created.</p>
                </div>
                <Button variant="primary" onClick={() => navigate('/pro/cases/new')}>
                    Create case
                </Button>
            </div>

            {error && <p className="text-[13px] text-red-600">{error}</p>}
            {cases === null && !error && <p className="text-[13px] text-ink3">Loading…</p>}

            {cases && cases.length === 0 && (
                <p className="font-serif italic text-[14px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent max-w-[520px]">
                    No cases yet. Create one to start an audit.
                </p>
            )}

            {cases && cases.length > 0 && (
                <ul className="border-t border-rule">
                    {cases.map(c => (
                        <li key={c.id} className="border-b border-rule">
                            <button
                                onClick={() => navigate(`/pro/cases/${c.id}`)}
                                className="group w-full text-left py-4 flex items-baseline gap-6 hover:bg-paper px-2"
                            >
                                <span className="font-serif italic text-[12px] text-accent shrink-0 w-20">
                                    {c.status}
                                </span>
                                <span className="flex-1 min-w-0">
                                    <span className="block font-serif text-[16px] text-ink truncate">
                                        {c.client_name || c.deceased_name || 'Unnamed case'}
                                    </span>
                                    <span className="block text-[11px] text-ink3 mt-0.5">
                                        {c.matter_reference ? `Ref ${c.matter_reference} · ` : ''}
                                        Updated {new Date(c.updated_at).toLocaleString('en-GB')}
                                    </span>
                                </span>
                                <span className="text-[12px] text-ink3 group-hover:text-accent shrink-0 font-serif italic">→</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </main>
    );
}

// ─────────────────────────────────────────────────────────────────────
// /pro/cases/new — minimal create form
// ─────────────────────────────────────────────────────────────────────

function ProCaseNew() {
    const [client, setClient] = useState('');
    const [deceased, setDeceased] = useState('');
    const [executor, setExecutor] = useState('');
    const [ref, setRef] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const created = await proCaseApi.create({
                client_name: client.trim() || undefined,
                deceased_name: deceased.trim() || undefined,
                executor_name: executor.trim() || undefined,
                matter_reference: ref.trim() || undefined,
            });
            navigate(`/pro/cases/${created.id}/audit`);
        } catch (err: any) {
            setError(err.message);
            setSubmitting(false);
        }
    };

    return (
        <main className="max-w-[640px] mx-auto px-8 py-12">
            <h1 className="font-serif text-[28px] text-ink mb-2">New case</h1>
            <p className="text-[12px] text-ink3 mb-8">All fields optional — you can add details later.</p>

            <form onSubmit={submit} className="space-y-5">
                <Field label="Client name" value={client} onChange={setClient} placeholder="e.g. Mrs J Holloway" />
                <Field label="Deceased name" value={deceased} onChange={setDeceased} placeholder="e.g. Mr R Holloway" />
                <Field label="Executor name" value={executor} onChange={setExecutor} placeholder="e.g. Mrs J Holloway (also executor)" />
                <Field label="Matter reference" value={ref} onChange={setRef} placeholder="e.g. HOL/2026/004" />

                {error && <p className="text-[13px] text-red-600">{error}</p>}

                <div className="flex gap-3 pt-4">
                    <Button variant="ghost" onClick={() => navigate('/pro/cases')}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? 'Creating…' : 'Create and start audit'}
                    </Button>
                </div>
            </form>
        </main>
    );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
    return (
        <label className="block">
            <div className="text-[11px] tracking-eyebrow uppercase text-ink3 mb-1.5">{label}</div>
            <input
                type="text"
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full border border-rule bg-white px-3 py-2.5 text-[13px] text-ink focus:border-accent focus:outline-none"
            />
        </label>
    );
}

// ─────────────────────────────────────────────────────────────────────
// /pro/cases/:caseId — case overview
// ─────────────────────────────────────────────────────────────────────

function ProCaseDetail({ caseId }: { caseId: string }) {
    const [c, setC] = useState<ProCase | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        proCaseApi.get(caseId).then(setC).catch(e => setError(e.message));
    }, [caseId]);

    if (error) return <main className="max-w-[640px] mx-auto px-8 py-12"><p className="text-red-600">{error}</p></main>;
    if (!c) return <main className="max-w-[640px] mx-auto px-8 py-12"><p className="text-ink3">Loading…</p></main>;

    return (
        <main className="max-w-[640px] mx-auto px-8 py-12">
            <button onClick={() => navigate('/pro/cases')} className="text-[12px] text-ink3 hover:text-ink mb-6">
                ← All cases
            </button>

            <h1 className="font-serif text-[28px] text-ink">
                {c.client_name || c.deceased_name || 'Unnamed case'}
            </h1>
            <p className="text-[11px] tracking-eyebrow uppercase text-accent mt-1">{c.status}</p>

            <dl className="mt-8 grid grid-cols-[160px_1fr] gap-x-6 gap-y-3 text-[13px]">
                <Detail label="Client" value={c.client_name} />
                <Detail label="Deceased" value={c.deceased_name} />
                <Detail label="Executor" value={c.executor_name} />
                <Detail label="Matter ref" value={c.matter_reference} />
                <Detail label="Updated" value={new Date(c.updated_at).toLocaleString('en-GB')} />
            </dl>

            <div className="mt-10 flex flex-wrap gap-3">
                <Button variant="primary" onClick={() => navigate(`/pro/cases/${c.id}/audit`)}>
                    {c.estate_id ? 'Continue audit' : 'Start audit'}
                </Button>
                {c.estate_id && (
                    <Button variant="secondary" onClick={() => navigate(`/pro/cases/${c.id}/report`)}>
                        View report
                    </Button>
                )}
                {c.status !== 'archived' && (
                    <Button
                        variant="ghost"
                        onClick={async () => {
                            const updated = await proCaseApi.update(c.id, { status: 'archived' });
                            setC(updated);
                        }}
                    >
                        Archive
                    </Button>
                )}
            </div>
        </main>
    );
}

function Detail({ label, value }: { label: string; value: string | null }) {
    return (
        <>
            <dt className="text-ink3 text-[11px] tracking-eyebrow uppercase">{label}</dt>
            <dd className="text-ink">{value ?? <span className="text-ink3 italic">—</span>}</dd>
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────
// /pro/cases/:caseId/audit — reuses ProbateWizard against the case's estate
// ─────────────────────────────────────────────────────────────────────

function ProCaseAudit({ caseId }: { caseId: string }) {
    const [estateId, setEstateId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        proCaseApi.startAudit(caseId)
            .then(({ estate_id }) => {
                if (cancelled) return;
                // Bind the wizard to this case's estate. The wizard reads the
                // estate id from localStorage to load /api/estates/:id, and
                // uses the case-scoped autosave key (`probate_wizard_autosave_v1:estate:<id>`)
                // for offline draft state. Pro case A and Pro case B therefore
                // never share a localStorage entry.
                localStorage.setItem(ESTATE_ID_KEY, estate_id);
                setEstateId(estate_id);
                setReady(true);
            })
            .catch(e => !cancelled && setError(e.message));
        return () => { cancelled = true; };
    }, [caseId]);

    if (error) return <main className="max-w-[640px] mx-auto px-8 py-12"><p className="text-red-600">{error}</p></main>;
    if (!ready || !estateId) return <main className="max-w-[640px] mx-auto px-8 py-12"><p className="text-ink3">Preparing audit…</p></main>;

    return (
        <div>
            <div className="bg-paper border-b border-rule px-8 py-2 flex items-center gap-3">
                <button onClick={() => navigate(`/pro/cases/${caseId}`)} className="text-[12px] text-ink3 hover:text-ink">
                    ← Case
                </button>
                <span className="text-[11px] text-ink3">Pro audit · case {caseId.slice(0, 8)}</span>
            </div>
            <ProbateWizard
                autoResume={true}
                storageKey={autosaveKeyForEstate(estateId)}
            />
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// /pro/cases/:caseId/report — read-only report view
// Implemented in ProCaseReportView.tsx; renders ReportBody directly,
// without mounting the editable wizard.
// ─────────────────────────────────────────────────────────────────────

function ProCaseReport({ caseId }: { caseId: string }) {
    return (
        <ProCaseReportView
            caseId={caseId}
            onBack={() => navigate(`/pro/cases/${caseId}`)}
            onEdit={() => navigate(`/pro/cases/${caseId}/audit`)}
        />
    );
}

// ─────────────────────────────────────────────────────────────────────
// Top-level dispatcher
// ─────────────────────────────────────────────────────────────────────

export const ProWorkspace: React.FC = () => {
    const [route, setRoute] = useState<Route>(() => parseProRoute(window.location.pathname));

    useEffect(() => {
        const handler = () => setRoute(parseProRoute(window.location.pathname));
        window.addEventListener('popstate', handler);
        return () => window.removeEventListener('popstate', handler);
    }, []);

    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <ProTopBar />
            {route.kind === 'cases' && <ProCasesList />}
            {route.kind === 'new' && <ProCaseNew />}
            {route.kind === 'detail' && <ProCaseDetail caseId={route.caseId} />}
            {route.kind === 'audit' && <ProCaseAudit caseId={route.caseId} />}
            {route.kind === 'report' && <ProCaseReport caseId={route.caseId} />}
        </div>
    );
};
